import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import { toast } from 'react-toastify';
import { calculateDistance } from '../utils/geo';

function EventsModal({ onClose, onViewOnMap, onOpenChat, onTriggerLogin, userLocation }) {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('agenda'); // 'agenda' or 'month'
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date()); // Default to today

    // Helpers for Month View
    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const days = [];

        // Pad start (0 = Sunday)
        for (let i = 0; i < firstDay.getDay(); i++) {
            days.push(null);
        }

        // Days
        for (let i = 1; i <= lastDay.getDate(); i++) {
            days.push(new Date(year, month, i));
        }

        return days;
    };

    const handlePrevMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    };

    const handleNextMonth = () => {
        setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
    };

    const getEventsForDay = (date) => {
        if (!date) return [];
        return events.filter(e => e.date.toDateString() === date.toDateString());
    };

    useEffect(() => {
        const fetchEvents = async () => {
            try {
                const now = new Date();
                const thirtyDaysFromNow = new Date();
                thirtyDaysFromNow.setDate(now.getDate() + 30);

                // --- 1. Fetch Scheduled Sessions (One-off games) ---
                const sessionsRef = collection(db, 'sessions');
                // Fetch ALL future sessions, then filter for "scheduled" client-side (avoids index issues)
                const sessionsQ = query(
                    sessionsRef,
                    where('scheduledStartTime', '>=', now),
                    orderBy('scheduledStartTime', 'asc'),
                    limit(50)
                );

                // --- 2. Fetch Club Events (One-off special events) ---
                const eventsRef = collection(db, 'events');
                const eventsQ = query(eventsRef);

                // --- 3. Fetch All Clubs (For recurring schedules) ---
                const clubsRef = collection(db, 'clubs');
                const clubsQ = query(clubsRef);

                const [sessionsSnapshot, eventsSnapshot, clubsSnapshot] = await Promise.all([
                    getDocs(sessionsQ),
                    getDocs(eventsQ),
                    getDocs(clubsQ)
                ]);

                let allEvents = [];

                // Process Sessions
                sessionsSnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    if (data.type === 'scheduled') {
                        allEvents.push({
                            id: doc.id,
                            type: 'session',
                            ...data,
                            date: data.scheduledStartTime.toDate()
                        });
                    }
                });

                // Process Club Events
                eventsSnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    const date = data.startTime.toDate();
                    if (date >= now) {
                        allEvents.push({
                            id: doc.id,
                            type: 'club_event',
                            ...data,
                            date: date
                        });
                    }
                });

                // Process Recurring Club Schedules
                const dayMap = { 'Sunday': 0, 'Monday': 1, 'Tuesday': 2, 'Wednesday': 3, 'Thursday': 4, 'Friday': 5, 'Saturday': 6 };

                clubsSnapshot.docs.forEach(doc => {
                    const club = { id: doc.id, ...doc.data() };

                    // Normalize Schedule: Handle both new 'schedule' array and legacy 'meetingDay'/'meetingTime'
                    let schedule = club.schedule;
                    if (!schedule && club.meetingDay && club.meetingTime) {
                        schedule = [{ day: club.meetingDay, time: club.meetingTime }];
                    }

                    if (!schedule || schedule.length === 0) return;

                    // Parse coordinates for distance check later
                    club.location = club.location || '';

                    schedule.forEach(slot => {
                        const targetDay = dayMap[slot.day];
                        if (targetDay === undefined) return;

                        // Generate instances for the next 90 days (approx 3 months) to support Month View navigation
                        for (let d = 0; d < 90; d++) {
                            const checkDate = new Date();
                            checkDate.setDate(now.getDate() + d);

                            if (checkDate.getDay() === targetDay) {
                                // Found a match! Create an event instance
                                const [hours, minutes] = slot.time.split(':').map(Number);
                                const instanceDate = new Date(checkDate);
                                instanceDate.setHours(hours, minutes, 0, 0);

                                // Skip if it's already passed today
                                if (instanceDate < now) continue;

                                allEvents.push({
                                    id: `${club.id}_${instanceDate.toISOString()}`, // Unique ID for key
                                    type: 'club_recurring',
                                    name: club.name,
                                    description: club.description,
                                    address: club.address,
                                    location: club.location, // Important for map view
                                    date: instanceDate,
                                    note: `Regular meeting: ${slot.day}s @ ${slot.time}`
                                });
                            }
                        }
                    });
                });

                const rawCount = allEvents.length;

                // --- Filter by Distance ---
                if (userLocation) {
                    allEvents = allEvents.filter(event => {
                        const [latStr, lngStr] = (event.location || '').split(',');
                        const lat = parseFloat(latStr);
                        const lng = parseFloat(lngStr);
                        // Only filter if location is valid
                        if (isNaN(lat) || isNaN(lng)) return false;
                        const distance = calculateDistance(userLocation.lat, userLocation.lng, lat, lng);
                        return distance <= 50; // 50 miles radius
                    });
                }

                // Sort by date
                allEvents.sort((a, b) => a.date - b.date);


                setEvents(allEvents);

            } catch (error) {
                console.error("Error fetching events:", error);
                toast.error("Could not load events.");
            } finally {
                setLoading(false);
            }
        };

        fetchEvents();
    }, [userLocation]);

    // Group events by day for Agenda View
    const groupedEvents = events.reduce((acc, event) => {
        const dateStr = event.date.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' });
        if (!acc[dateStr]) acc[dateStr] = [];
        acc[dateStr].push(event);
        return acc;
    }, {});

    return (
        <div className="fixed inset-0 bg-black/50 z-[1200] flex items-center justify-center p-4 animate-fade-in">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-indigo-600 text-white">
                    <h2 className="text-xl font-bold flex items-center gap-2">
                        {selectedEvent ? 'ℹ️ Event Details' : '📅 Events Calendar'}
                    </h2>
                    <button
                        onClick={selectedEvent ? () => setSelectedEvent(null) : onClose}
                        className="text-white/80 hover:text-white hover:bg-white/10 rounded-full p-1 transition-colors"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            {selectedEvent ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            )}
                        </svg>
                    </button>
                </div>

                {selectedEvent ? (
                    /* --- DETAIL VIEW --- */
                    <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
                        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
                            <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedEvent.name}</h3>

                            {/* Tags */}
                            <div className="flex gap-2 mb-4">
                                {selectedEvent.type === 'club_recurring' && <span className="text-xs bg-purple-100 text-purple-700 px-2 py-1 rounded-full font-bold">CLUB RECURRING</span>}
                                {selectedEvent.type === 'club_event' && <span className="text-xs bg-orange-100 text-orange-700 px-2 py-1 rounded-full font-bold">CLUB EVENT</span>}
                                {selectedEvent.type === 'session' && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-bold">OPEN SESSION</span>}
                                {selectedEvent.isKidFriendly && <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">👶 Kid Friendly</span>}
                                {selectedEvent.isBeginnerFriendly && <span className="text-xs bg-teal-100 text-teal-700 px-2 py-1 rounded-full">🎓 Beginner Friendly</span>}
                            </div>

                            <div className="space-y-4 text-gray-700">
                                <div className="flex items-start gap-3">
                                    <span className="text-xl">🕒</span>
                                    <div>
                                        <p className="font-bold">Time</p>
                                        <p>{selectedEvent.date.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })}</p>
                                        <p>{selectedEvent.date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <span className="text-xl">📍</span>
                                    <div>
                                        <p className="font-bold">Location</p>
                                        <p>{selectedEvent.address}</p>
                                        <div className="flex gap-2 mt-2">
                                            <a
                                                href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(selectedEvent.address || '')}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded border border-gray-300 transition-colors"
                                            >
                                                🚗 Navigate
                                            </a>
                                            <button
                                                onClick={() => {
                                                    if (!user) {
                                                        onTriggerLogin();
                                                        onClose();
                                                    } else {
                                                        onOpenChat(selectedEvent);
                                                    }
                                                }}
                                                className="text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-3 py-1.5 rounded border border-blue-200 transition-colors flex items-center gap-1"
                                            >
                                                💬 Chat
                                            </button>
                                            <button
                                                onClick={() => {
                                                    onViewOnMap(selectedEvent);
                                                    onClose();
                                                }}
                                                className="text-xs bg-indigo-50 hover:bg-indigo-100 text-indigo-700 px-3 py-1.5 rounded border border-indigo-200 transition-colors"
                                            >
                                                🗺️ View on Map
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {(selectedEvent.description || selectedEvent.note) && (
                                    <div className="flex items-start gap-3">
                                        <span className="text-xl">📝</span>
                                        <div>
                                            <p className="font-bold">Description</p>
                                            <p className="whitespace-pre-wrap text-sm">{selectedEvent.description || selectedEvent.note}</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <button
                            onClick={() => setSelectedEvent(null)}
                            className="w-full mt-6 bg-white border border-gray-300 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
                        >
                            ← Back to List
                        </button>
                    </div>
                ) : (
                    /* --- LIST VIEW --- */
                    <>
                        {/* Tabs */}
                        <div className="flex border-b border-gray-200">
                            <button
                                className={`flex-1 py-3 text-sm font-semibold ${activeTab === 'agenda' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                                onClick={() => setActiveTab('agenda')}
                            >
                                List View
                            </button>
                            <button
                                className={`flex-1 py-3 text-sm font-semibold ${activeTab === 'month' ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`}
                                onClick={() => setActiveTab('month')}
                            >
                                Month View
                            </button>
                        </div>

                        {/* Content */}
                        <div className="flex-1 overflow-y-auto p-4 bg-gray-50">
                            {activeTab === 'month' ? (
                                <div className="flex flex-col h-full">
                                    {/* Month Nav */}
                                    <div className="flex justify-between items-center mb-4 px-2">
                                        <button onClick={handlePrevMonth} className="p-2 hover:bg-gray-200 rounded-full">←</button>
                                        <h3 className="font-bold text-lg text-gray-800">
                                            {currentMonth.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                                        </h3>
                                        <button onClick={handleNextMonth} className="p-2 hover:bg-gray-200 rounded-full">→</button>
                                    </div>

                                    {/* Grid Header */}
                                    <div className="grid grid-cols-7 mb-2 text-center text-xs font-bold text-gray-500">
                                        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d}>{d}</div>)}
                                    </div>

                                    {/* Calendar Grid */}
                                    <div className="grid grid-cols-7 gap-1 auto-rows-[60px]">
                                        {getDaysInMonth(currentMonth).map((day, idx) => {
                                            const dayEvents = day ? getEventsForDay(day) : [];
                                            const isToday = day && day.toDateString() === new Date().toDateString();
                                            const isSelected = day && selectedDate && day.toDateString() === selectedDate.toDateString();

                                            return (
                                                <div
                                                    key={idx}
                                                    className={`
                                                        border rounded p-1 flex flex-col items-center cursor-pointer relative bg-white transition-all
                                                        ${!day ? 'invisible border-none' : ''}
                                                        ${isSelected ? 'border-2 border-indigo-500 ring-1 ring-indigo-200 z-10' : 'border-gray-100 hover:border-indigo-300'}
                                                    `}
                                                    onClick={() => day && setSelectedDate(day)}
                                                >
                                                    {day && (
                                                        <>
                                                            <span className={`text-xs font-medium w-6 h-6 flex items-center justify-center rounded-full mb-1 ${isToday ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-700'}`}>
                                                                {day.getDate()}
                                                            </span>
                                                            <div className="flex gap-1 flex-wrap justify-center content-start w-full px-1">
                                                                {dayEvents.slice(0, 5).map(e => (
                                                                    <div
                                                                        key={e.id}
                                                                        className={`w-2 h-2 rounded-full ${e.type === 'session' ? 'bg-blue-500' : e.type.includes('club') ? 'bg-purple-500' : 'bg-orange-500'}`}
                                                                        title={e.name}
                                                                    />
                                                                ))}
                                                                {dayEvents.length > 5 && <span className="text-[8px] text-gray-400 leading-none">+</span>}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Selected Day Agenda */}
                                    <div className="mt-6 border-t border-gray-200 pt-4">
                                        <h4 className="font-bold text-gray-800 mb-3 flex items-center gap-2">
                                            {selectedDate ? (
                                                <>
                                                    <span className="text-xl">📅</span>
                                                    {selectedDate.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                                                </>
                                            ) : (
                                                <span className="text-gray-400 italic font-normal">Select a date to view events</span>
                                            )}
                                        </h4>
                                        <div className="space-y-3">
                                            {selectedDate && getEventsForDay(selectedDate).length === 0 && <p className="text-gray-500 text-sm italic pl-2">No events scheduled.</p>}
                                            {selectedDate && getEventsForDay(selectedDate).map(event => (
                                                <div
                                                    key={event.id}
                                                    className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-all cursor-pointer flex gap-3 group"
                                                    onClick={() => setSelectedEvent(event)}
                                                >
                                                    <div className="flex-shrink-0 w-16 text-center flex flex-col justify-center border-r border-gray-100 pr-3">
                                                        <span className="text-lg font-bold text-indigo-600 group-hover:scale-110 transition-transform">
                                                            {event.date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                                        </span>
                                                        {event.type.includes('club') ? (
                                                            <span className="text-[10px] bg-purple-100 text-purple-700 px-1 rounded-full mt-1 font-bold">CLUB</span>
                                                        ) : (
                                                            <span className="text-[10px] bg-blue-100 text-blue-700 px-1 rounded-full mt-1 font-bold">GAME</span>
                                                        )}
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex justify-between items-start">
                                                            <h4 className="font-semibold text-gray-900 truncate group-hover:text-indigo-600 transition-colors">{event.name}</h4>
                                                        </div>
                                                        <p className="text-sm text-gray-600 truncate">📍 {event.address}</p>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : loading ? (
                                <div className="flex justify-center py-10"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div></div>
                            ) : events.length === 0 ? (
                                <div className="text-center py-10 text-gray-500">
                                    <p className="text-4xl mb-2">🗓️</p>
                                    <p>No upcoming events found within 50 miles.</p>
                                </div>
                            ) : (
                                <div className="space-y-6">
                                    {Object.entries(groupedEvents).map(([dateStr, dayEvents]) => (
                                        <div key={dateStr}>
                                            <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wider mb-2 sticky top-0 bg-gray-50 py-1 z-10">
                                                {dateStr}
                                            </h3>
                                            <div className="space-y-3">
                                                {dayEvents.map(event => (
                                                    <div
                                                        key={event.id}
                                                        className="bg-white p-3 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow cursor-pointer flex gap-3"
                                                        onClick={() => setSelectedEvent(event)}
                                                    >
                                                        <div className="flex-shrink-0 w-16 text-center flex flex-col justify-center border-r border-gray-100 pr-3">
                                                            <span className="text-lg font-bold text-indigo-600">
                                                                {event.date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                                                            </span>
                                                            {event.type === 'club_recurring' && <span className="text-[10px] bg-purple-100 text-purple-700 px-1 rounded-full mt-1">CLUB</span>}
                                                            {event.type === 'club_event' && <span className="text-[10px] bg-orange-100 text-orange-700 px-1 rounded-full mt-1">EVENT</span>}
                                                            {event.type === 'session' && <span className="text-[10px] bg-blue-100 text-blue-700 px-1 rounded-full mt-1">OPEN</span>}
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="font-semibold text-gray-900 truncate">{event.name}</h4>
                                                            <p className="text-sm text-gray-600 truncate">📍 {event.address}</p>
                                                            {(event.note || event.description) && <p className="text-xs text-gray-500 mt-1 line-clamp-1">{event.note || event.description}</p>}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default EventsModal;
